import { useEffect, useState } from 'react'
import type { IntervalTimerState } from '@gymboard/types'
import { timerDisplay, type TimerDisplayKey } from '../../lib/timer-display'

const PHASE: Record<TimerDisplayKey, { label: string; ink: string; fill: string }> = {
  ready: { label: 'Get ready', ink: '#D9B68A', fill: 'rgba(217, 182, 138, 0.28)' },
  work: { label: 'Work', ink: '#FF7A6E', fill: 'rgba(201, 58, 48, 0.5)' },
  rest: { label: 'Rest', ink: '#3CC8D0', fill: 'rgba(60, 200, 208, 0.3)' },
  done: { label: 'Done', ink: '#30D158', fill: 'rgba(48, 209, 88, 0.3)' },
}

/**
 * The coach's interval timer, in the header beside the class clock (the
 * Spotlight screen keeps the corners free for the stage and the QR dock).
 * Ticks locally from the absolute server start, so every screen agrees.
 * The fill drains as the phase runs out: a transform, so it stays smooth
 * on weak TV hardware.
 */
export default function IntervalPill({ timer, clockOffsetMs }: { timer: IntervalTimerState | null; clockOffsetMs: number }) {
  const [now, setNow] = useState(() => Date.now() + clockOffsetMs)
  useEffect(() => {
    if (!timer) return
    const id = setInterval(() => setNow(Date.now() + clockOffsetMs), 200)
    return () => clearInterval(id)
  }, [timer, clockOffsetMs])

  const d = timerDisplay(timer, now)
  if (!d || !d.visible) return null
  const p = PHASE[d.key]

  return (
    <div
      className="sp-ivl"
      role="timer"
      aria-live="polite"
      aria-label={`${p.label}, ${d.seconds} seconds, round ${d.round} of ${d.rounds}`}
    >
      <span className="sp-ivl-fill" style={{ background: p.fill, transform: `scaleX(${d.key === 'done' ? 1 : d.progress})` }} />
      <span className="sp-ivl-label" style={{ color: p.ink }}>{p.label}</span>
      {d.key !== 'done' && <b>{d.seconds}</b>}
      <span className="sp-ivl-round">
        Round {d.round} of {d.rounds}
        {timer?.label ? <small>{timer.label}</small> : null}
      </span>
    </div>
  )
}
